<i18n>
{
  "fr": {
    "configuratorEditor": {
      "tabs": {
        "product": "Produit",
        "content": "Personnalisation"
      },
      "changeThemeAlert": "Vous vous apprêtez à modifier le style de votre gobelet. Vous perdrez toute la configuration en cours de personnalisation.",
      "themePickerButton": "Changer de visuel"
    }
  }
}
</i18n>

<template>
  <div
    ref="scrollContainer"
    :class="[
      `${BLOCK_SELECTOR}__scroll-container`,
      { [`${BLOCK_SELECTOR}__scroll-container--is-docked`]: isDocked },
    ]"
  >
    <!-- Spacer: captures horizontal touch and forwards to 3D view -->
    <div
      v-if="!isDocked"
      ref="spacer"
      :class="`${BLOCK_SELECTOR}__spacer`"
      @touchstart="onSpacerTouchStart"
      @touchend="onSpacerTouchEnd"
    >
      <div
        v-if="!isDocked"
        :class="`${BLOCK_SELECTOR}__toolbar`"
        :style="{ opacity: toolbarOpacity, bottom: `-${toolbarBottom / 2}px` }"
      >
        <div :class="`${BLOCK_SELECTOR}__toolbar-spacer`"></div>

        <span
          v-if="themeType === ThemeType.GENERIC"
          :class="`${BLOCK_SELECTOR}__theme-picker-button-wrapper`"
        >
          <BaseBtn
            :class="`${BLOCK_SELECTOR}__theme-picker-button`"
            link
            @click="handleThemeModalOpeningRequest()"
          >
            {{ $t('configuratorEditor.themePickerButton') }}
          </BaseBtn>
          <div :class="`${BLOCK_SELECTOR}__theme-picker-button-icon`"></div>
        </span>

        <ConfiguratorProject
          :class="`${BLOCK_SELECTOR}__configurator-project`"
        />
      </div>
    </div>

    <div
      :class="[
        BLOCK_SELECTOR,
        {
          [`${BLOCK_SELECTOR}--is-docked`]: isDocked,
        },
      ]"
    >
      <div v-if="!isDocked" :class="`${BLOCK_SELECTOR}__handle`"></div>
      <ConfiguratorEditorTabs
        v-if="!isDocked"
        v-model="activeTab"
        :tabs="tabs"
      />
      <ConfiguratorEditorThemeChangeModal
        :is-active="isThemeChangeModalActive"
        @close="isThemeChangeModalActive = false"
        @confirm="onThemeChangeConfirmed"
      />

      <keep-alive>
        <div :class="`${BLOCK_SELECTOR}__content-wrapper`">
          <ConfiguratorEditorProductTab
            v-if="isDocked || activeTab === 'product'"
            :class="`${BLOCK_SELECTOR}__content ${BLOCK_SELECTOR}__content--product`"
            :is-docked="isDocked"
            @request-open-theme-picker-modal="handleThemeModalOpeningRequest()"
          />

          <ConfiguratorEditorGraphismTab
            v-if="isDocked || activeTab === 'content'"
            ref="graphismTab"
            :class="`${BLOCK_SELECTOR}__content ${BLOCK_SELECTOR}__content--graphism`"
            :is-docked="isDocked"
          />

          <ConfiguratorFooter
            v-if="isDocked"
            :class="`${BLOCK_SELECTOR}__docked-footer`"
          />
        </div>
      </keep-alive>

      <!-- When docked: render modal in place -->
      <keep-alive v-if="isDocked">
        <ConfiguratorEditorThemePickerModal
          v-if="isEscapeClosableActive"
          :class="`${BLOCK_SELECTOR}__theme-selection-modal`"
          :is-docked="isDocked"
          @close="closeEscapeClosable"
        />
      </keep-alive>

      <!-- When not docked: render modal via portal to app footer -->
      <Portal v-else append to="portal-container--app-footer">
        <keep-alive>
          <ConfiguratorEditorThemePickerModal
            v-if="isHistoryBackClosableActive"
            :is-docked="isDocked"
            @close="closeWithHistory"
          />
        </keep-alive>
      </Portal>
    </div>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  ConfiguratorGetters,
} from '../../store/modules/configurator';
import { ThemeType } from '../../api/static/ThemeType';
import { Breakpoint } from '../../utils/breakpoints';
import ConfiguratorEditorTabs from './ConfiguratorEditorTabs';
import ConfiguratorEditorProductTab from './ConfiguratorEditorProductTab';
import ConfiguratorEditorGraphismTab from './ConfiguratorEditorGraphismTab';
import ConfiguratorFooter from './ConfiguratorFooter';
import ConfiguratorEditorThemePickerModal from './Modals/ConfiguratorEditorThemePickerModal';
import ConfiguratorEditorThemeChangeModal from './Modals/ConfiguratorEditorThemeChangeModal';
import SpacerTouchRotation from '../../mixins/SpacerTouchRotation';
import HistoryBackClosable from '../../mixins/HistoryBackClosable';
import EscapeClosable from '../../mixins/EscapeClosable';
import ConfiguratorProject from './ConfiguratorProject';
import BaseBtn from '../BaseBtn';

export default {
  name: 'ConfiguratorEditor',
  components: {
    ConfiguratorEditorTabs,
    ConfiguratorEditorProductTab,
    ConfiguratorEditorGraphismTab,
    ConfiguratorFooter,
    ConfiguratorEditorThemePickerModal,
    ConfiguratorEditorThemeChangeModal,
    ConfiguratorProject,
    BaseBtn,
  },
  mixins: [SpacerTouchRotation, HistoryBackClosable, EscapeClosable],
  data() {
    return {
      BLOCK_SELECTOR: 'configurator-editor',
      TOOLBAR_FADE_DISTANCE: 64, // distance in pixels
      activeTab: 'product',
      tabs: [
        {
          label: this.$t('configuratorEditor.tabs.product'),
          key: 'product',
          icon: '',
        },
        {
          label: this.$t('configuratorEditor.tabs.content'),
          key: 'content',
          icon: '',
        },
      ],
      isThemeChangeModalActive: false,
      toolbarOpacity: 1,
      toolbarBottom: 0,
      ThemeType,
    };
  },
  computed: {
    isDocked() {
      return this.$matchMedia[Breakpoint.CONFIGURATOR_L];
    },
    ...mapState({
      themeType: 'themeType',
    }),
    ...mapGetters({
      isCurrentProjectDirty:
        ConfiguratorGetters.IS_CURRENT_CUSTOMER_PROJECT_DIRTY,
    }),
  },
  watch: {
    activeTab() {
      if (this.themeType === ThemeType.GENERIC) {
        this.$nextTick(() => {
          this.$el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
      }
    },
  },
  mounted() {
    const searchParams = new URLSearchParams(globalThis.location.search);
    if (searchParams.has('themeType')) {
      this.activeTab = 'product';
    } else {
      this.activeTab = 'content';
    }

    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll);
    }
  },
  beforeDestroy() {
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener(
        'scroll',
        this.handleScroll
      );
    }
  },
  methods: {
    handleScroll() {
      if (!this.$refs.scrollContainer || this.isDocked) {
        return;
      }

      const scrollTop = this.$refs.scrollContainer.scrollTop;
      const fadeDistanceInPixels = this.TOOLBAR_FADE_DISTANCE;

      const opacity = Math.max(0, 1 - scrollTop / fadeDistanceInPixels);

      this.toolbarOpacity = opacity;
      this.toolbarBottom = scrollTop;
    },
    onThemeChangeConfirmed() {
      this.isThemeChangeModalActive = false;
      if (this.isDocked) {
        this.openEscapeClosable();
      } else {
        this.openWithHistory();
      }
    },
    handleThemeModalOpeningRequest() {
      // Show confirmation only if the user has a custom theme AND has made changes
      if (this.themeType === ThemeType.CUSTOM && this.isCurrentProjectDirty) {
        this.isThemeChangeModalActive = true;
      } else {
        if (this.isDocked) {
          this.openEscapeClosable();
        } else {
          this.openWithHistory();
        }
      }
    },
    onHistoryBackClosableClosed() {
      this.afterThemePickerModalClosed();
    },
    onEscapeClosableClosed() {
      this.afterThemePickerModalClosed();
    },
    afterThemePickerModalClosed() {
      if (this.themeType === ThemeType.GENERIC) {
        this.activeTab = 'content';
        this.$nextTick(() => {
          this.$refs.graphismTab?.focusFirstField();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Scrollable container for mobile view
.configurator-editor__scroll-container {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  pointer-events: auto; // Enables scroll but blocks 3D interaction through spacer
  display: flex;
  flex-direction: column;

  @include breakpoint($breakpoint-l) {
    overflow: visible;
  }

  &--is-docked {
    overflow: visible;
    width: 100%;
    max-width: 44rem;
  }
}

.configurator-editor {
  $block-selector: &;

  background-color: $color-white;
  pointer-events: auto;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 0.875rem 0.875rem 0 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;

  @include breakpoint($breakpoint-l) {
    background-color: transparent;
    border-radius: 1.25rem;
  }

  &--is-docked {
    height: 100%;
    box-shadow: none;
  }

  // Spacer: captures touch events to forward horizontal movement to 3D
  &__spacer {
    min-height: 70%;
    flex-grow: 1;
    flex-shrink: 0;
    pointer-events: auto; // Capture touch to forward to 3D canvas
    position: relative;
  }

  &__toolbar {
    height: 4.375rem;
    width: 100%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0.75rem 1rem;
    position: absolute;
    left: 0;
    right: 0;
  }

  &__handle {
    width: 2.5rem;
    height: 0.25rem;
    background-color: $color-gray-silver;
    border-radius: 0.125rem;
    margin: 0.5rem auto;
  }

  &__header {
    flex-shrink: 0;
  }

  &__content-wrapper {
    overflow-y: auto;
    min-height: 0;
    border-radius: 1.875rem;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__docked-footer {
    position: sticky;
    bottom: 0;
  }

  &__toolbar-spacer {
    width: 2.75rem;
  }

  &__theme-picker-button-wrapper {
    flex: 1;
    display: inline-flex;
    justify-content: center;
    align-self: flex-end;
    gap: 0.5rem;
  }

  &__theme-picker-button {
    text-transform: uppercase;
    text-decoration: underline;
    color: $color-arapawa;
    font-size: 1rem;

    &:hover {
      text-decoration: none;
    }
  }

  &__theme-picker-button-icon {
    &::after {
      @extend %icon;
      @extend %icon--replay;

      color: $color-arapawa;
      font-size: 0.875rem;
    }
  }

  &__configurator-project {
    width: fit-content;

    &::v-deep .configurator-project__button {
      height: 2.75rem;
      width: 2.75rem;
      border-radius: 50%;
    }
  }
}
</style>
